import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import GoogleSignInButton from './GoogleSignInButton';
import GoogleAuthService from '@dashboard/services/googleAuth';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { getGclid } from '@dashboard/lib/gclid';
import storage from '@dashboard/services/storage';
import { authRedirectPath } from '@dashboard/components/AuthForm';

const GoogleSignIn = ({
  mode = 'login', // 'login' | 'signup' | 'invite'
  onSuccess,
  onError,
  marketingConsent = false,
  className,
  size = 'full',
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showAccountLinking, setShowAccountLinking] = useState(false);
  const [linkingData, setLinkingData] = useState(null);
  const { setLogged } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const redirectPath = authRedirectPath(location.search);
  const invite = searchParams.get('invite') || searchParams.get('inviteToken');
  const team = searchParams.get('team');

  const getButtonText = () => {
    switch (mode) {
      case 'signup':
        return 'Sign up with Google';
      case 'invite':
        return 'Accept invite with Google';
      default:
        return 'Continue with Google';
    }
  };

  const getReturnPath = () => {
    switch (mode) {
      case 'invite':
        return `/accept-invite`;
      case 'signup':
        return '/signup';
      case 'login':
      default:
        return '/login';
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);

    try {
      const redirectURI = GoogleAuthService.getRedirectURI();
      const state = GoogleAuthService.generateState({
        redirectPath,
        returnPath: getReturnPath(),
        invite,
        team,
      });

      // Store mode and marketingConsent in localStorage for callback handling
      localStorage.setItem('google_oauth_mode', mode);
      localStorage.setItem('google_oauth_marketing_consent', marketingConsent.toString());

      // Capture GCLID and sessionId for attribution tracking
      const gclid = getGclid();
      const sessionId = storage.local.getItem('CheckoutPage.sessionId');

      if (gclid) {
        localStorage.setItem('google_oauth_gclid', gclid);
      }
      if (sessionId) {
        localStorage.setItem('google_oauth_sessionId', sessionId);
      }

      // Redirect to Google OAuth
      await GoogleAuthService.startOAuthFlow({
        redirectURI,
        state,
      });
    } catch (error) {
      console.error('Failed to start OAuth flow:', error);
      onError?.(error);
      setIsLoading(false);
    }
  };

  const handleAccountLinking = async password => {
    if (!linkingData) return;

    try {
      setIsLoading(true);

      const response = await GoogleAuthService.linkAccount(
        linkingData.linkToken,
        linkingData.email,
        password,
        linkingData.invite,
      );

      if (response.auth && response.data) {
        setLogged({
          user: response.data.user,
          seller: response.data.user.currentSeller,
          token: response.data.token,
        });

        onSuccess?.({
          user: response.data.user,
          isNewUser: false,
          accountLinked: true,
          inviteAccepted: response.data.inviteAccepted,
          needsOnboarding: response.data.needsOnboarding,
        });

        history.push(redirectPath);
      }
    } catch (error) {
      console.error('Account linking error:', error);
      onError?.(error);
    } finally {
      setIsLoading(false);
      setShowAccountLinking(false);
      setLinkingData(null);
    }
  };

  if (showAccountLinking && linkingData) {
    return (
      <AccountLinkingModal
        email={linkingData.email}
        message={linkingData.message}
        hasInvite={linkingData.hasInvite}
        onSubmit={handleAccountLinking}
        onCancel={() => {
          setShowAccountLinking(false);
          setLinkingData(null);
        }}
        isLoading={isLoading}
      />
    );
  }

  return (
    <GoogleSignInButton
      onClick={handleGoogleSignIn}
      disabled={isLoading}
      size={size}
      className={className}
      {...props}
    >
      {getButtonText()}
    </GoogleSignInButton>
  );
};

// Account linking modal component
const AccountLinkingModal = ({ email, message, hasInvite, onSubmit, onCancel, isLoading }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setError('');
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err.message || 'Failed to link account');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-background rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-semibold mb-4">
          {hasInvite ? 'Link Account & Join Team' : 'Link Google Account'}
        </h2>

        <p className="text-gray-600 dark:text-muted-foreground mb-4">{message}</p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-muted-foreground mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              disabled
              className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-md bg-gray-50 dark:bg-card text-gray-500 dark:text-muted-foreground"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-muted-foreground mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password to confirm"
              className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-md focus:ring-2 focus:ring-ring focus:border-foreground"
              disabled={isLoading}
            />
          </div>

          {error && <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-muted-foreground bg-white dark:bg-background border border-gray-300 dark:border-border rounded-md hover:bg-gray-50 dark:hover:bg-muted focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {isLoading ? 'Linking...' : hasInvite ? 'Link & Join' : 'Link Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoogleSignIn;
